import * as React from 'react';
import { useRef, useState } from 'react';
import SEO from '_components/seo_v3';
import { createFAQData, createBreadcrumbData, createOneShiftAutoDealerData } from '_utils/structuredData';

import { Col, Row } from 'antd';

import LeadPageUserReview from '_components/LeadPageUserReview';
import LeadPageProofSection from '_components/LeadPageProofSection';
import LeadPageFaq from '_components/LeadPageFaq';
import LeadPageServices from '_components/LeadPageServices';
import LeadPageFooter from '_components/LeadPageFooter';
import submitDetailsIcon from '_images/carousell-sq-sell-car-icon-submit-details.svg';
import bidding24hrsIcon from '_images/carousell-sq-sell-car-icon-24hrs-bidding.svg';
import priceMatchAndInspectionsIcon from '_images/carousell-os-car-valuation-icon-price-match-and-inspections.svg';
import transactWhenPriceIsRightIcon from '_images/carousell-os-car-valuation-icon-transact-when-price-is-right.svg';
import LeadPageHzSection from '_components/LeadPageHzSection';
import LeadPageTabSlider from '_components/LeadPageTabSlider';

import faqImg from '_images/carousell-os-car-valuation-dealer-silhouette.svg';

import carValuationMin from '_images/carousell-os-car-valuation-min.png';

import convenientAndHassleFree from '_images/carousell-os-car-valuation-convenient-and-hassle-free.svg';
import valuationConsumerProtection from '_images/carousell-os-car-valuation-consumer-protection.svg';
import obligatoryAndTransparent from '_images/carousell-os-car-valuation-non-obligatory-and-transparent.svg';
import LeadPageCarValuationForm from '_components/LeadPageCarValuationForm';
import Header from '_components/HeaderV2';

const carValuationFirstSectionData = [
  {
    icon: submitDetailsIcon,
    iconAlt: 'Submit your details',
    title: (
      <>
        Submit your <br /> details
      </>
    ),
    description: (
      <>
        Provide your information as required by{' '}
        <a
          href="https://vrl.lta.gov.sg/lta/vrl/action/pubfunc?ID=EnquireRebateBeforeDeReg"
          target="_blank"
          rel="noreferrer"
        >
          LTA
        </a>{' '}
        for us to retrieve your car details
      </>
    )
  },
  {
    icon: bidding24hrsIcon,
    iconAlt: '24 hours bidding',
    title: (
      <>
        24 hours
        <br className="hidden md:block" /> bidding
      </>
    ),
    description:
      'We will help you get the highest valuation and prices for your car through OneShift\'s network of 500+ trusted dealers'
  },
  {
    icon: priceMatchAndInspectionsIcon,
    iconAlt: 'Price match and inspections',
    title: (
      <>
        Price match <br className="hidden md:block" />
        and inspections
      </>
    ),
    description:
      'We will arrange for a non-obligatory viewing and inspection of your car with the dealer who gave the best car valuation if you decide to sell.'
  },
  {
    icon: transactWhenPriceIsRightIcon,
    iconAlt: 'Transact when price is right!',
    title: (
      <>
        Transact when <br className="hidden md:block" />
        price is right!
      </>
    ),
    description:
      'Sell your car only when you\'re happy with the price and get paid immediately by the dealer. It\'s that simple!'
  }
];

const tabData = [
  {
    label: 'Convenient and Hassle free',
    description:
      'We know visiting multiple car dealerships can be a real hassle! We will help you obtain the highest valuation and price for your car within 24 hours from our network of 500+ certified dealers from the comfort of your home.',
    image: convenientAndHassleFree,
  },
  {
    label: 'Consumer Protection',
    description:
      'We curate and vet all our partner dealers to ensure that you have a great experience. We employ strict quality control when validating the used car dealers in our panel and only work  with trusted and reputable LTA-approved partners.',
    image: valuationConsumerProtection,
  },
  {
    label: 'Non Obligatory and Transparent',
    description:
      'We respect our customers and understand that at times, you may have doubts or need more time to decide. Simply let us know because we will never force a sale on you. Our dealer partners will only transact with you when you are satisfied with the final offer.',
    image: obligatoryAndTransparent,
  },
];

const userReviews = [
  {
    star: 5,
    content:
      'Excellent service. No hassle. Fast and reliable. Absolutely recommended. ',
    author: 'Radzaly Bin Mohd Noor',
  },
  {
    star: 5,
    content:
      'Excellent. It gives me better money for my scrap vehicle. Thank you guys',
    author: 'Jefri Sidik Soerdi',
  },
  {
    star: 5,
    content:
      'The entire experience from registering my car on OneShift to the handing over to the dealer was very pleasant.  The process at OneShift was efficient and transparent. Special thanks to RenAi from OneShift who was very detailed and helpful.',
    author: 'Charmaine Ang',
  },
  {
    star: 5,
    content:
      'Great experience. Mr Pan the car dealer from Car Enthusiast is very helpful and friendly. Attitude towards client is awesome.. ',
    author: 'YEO CHOON WEI',
  },
  {
    star: 5,
    content:
      'Good response and support from Angie at One Shift when I wanted to sell off my old Camry which was due to be scrapped in early February. Would recommend this platform for those wanting to buy or sell their cars. ',
    author: 'Bahl Alok',
  },
  {
    star: 5,
    content:
      'Prompt and Fast. Service officer Muhd Yazid was very helpful in guiding the sales, and when urgent assistance is required! Kudos to officer for being able to address all queries.',
    author: 'Arof Bin Yaacob',
  },
  {
    star: 5,
    content:
      'It was very smooth and fast deal.\\nDefinitely will use one shift again to buy or sell car. ',
    author: 'Lorrin',
  },
  { star: 5, content: 'Great and prompt service as always!', author: 'Nair' },
  {
    star: 5,
    content:
      'Very satisfied by the service and convenience. Ii sold my car in 2 days.',
    author: 'Tan Wee Ann',
  },
  {
    star: 5,
    content: 'Very happy with the ease and smooth experience!',
    author: 'Minarni',
  },
];

const faqData = [
  {
    title: 'How does the process to get a car valuation online work?',
    content: (
      <div>
        <b>
          Enjoy a hassle free experience when you get a price valuation from us!
        </b>
        <ul className="mt-4 list-disc pl-8">
          <li>
            Simply provide us your car details and we will put it up for
            valuation via our network of 500+ trusted dealers to get you the
            highest price in 24 hours.
          </li>
          <li>
            When you are satisfied with the price, an appointment will be set up
            with the winning dealer to evaluate and inspect the condition of
            your car ; afterwhich, a final offer will be given to you on the
            spot.
          </li>
          <li>
            You may transact immediately if you are happy with the final offer.
          </li>
        </ul>
      </div>
    ),
  },
  {
    title: 'How long is the waiting time for my car valuation to be ready?',
    content:
      'We typically take 24 hours to obtain the best and highest valuation from as many of our dealer partners as possible. However, feel free to let us know if you require a quicker turnaround time. Our fastest valuation to date was completed in 2 hours when the seller was able to provide the full details of the car.',
  },
  {
    title: 'Where will the viewing take place?',
    content:
      'The viewing and inspection of your car can take place either at our office premises or at your preferred location. Simply let us know your preference!',
  },
  {
    title:
      'What are the fees involved to sell my car through OneShift? How do I calculate this? Are there any hidden fees involved?',
    content: (
      <div>
        Getting the <strong>BEST &amp; HIGHEST</strong> prices through our
        bidding platform is absolutely free-of-charge. Rest assured that you are
        under no obligations to sell your car with us. However, we will charge a{' '}
        <u>Seller Success Fee of $109</u> (including GST) if you decide to sell
        your car with us. This service fee will be waived if your car is more
        than 9 years old.
      </div>
    ),
  },
  {
    title:
      'What if I do not want to go through a dealer and want to sell it to other direct buyers?',
    content:
      'Once you get an indicative price of your car, you can list it directly on Carousell to sell it to other direct car buyers, or OneShift can represent you to sell your car on your behalf. We will list and manage the enquiries and viewings so you can sit back and relax. Learn more',
  },
  {
    title: 'What happens next if I am satisfied with my car valuation?',
    content:
      'Simply let our client success manager know which offer you would like to proceed with and we will arrange for a non-obligatory viewing/inspection with the winning dealer to verify the condition of your car.',
  },
  {
    title:
      'Can I reject the dealer\'s final offer if there is a reduction after the viewing/inspection?',
    content:
      'Yes, of course! You are under no obligation to accept any price reductions if they do not meet your expectations. The dealer can only negotiate for a lower price if there are faults or defects with the car that were previously undiscovered. In the unlikely event that a resolution cannot be reached, we will be glad to arrange for another viewing/inspection with the other dealers who have also made offers.',
  },
  {
    title:
      'Do I need to hand over my car immediately upon signing the sales contract?',
    content:
      'The quotes given by our dealers will be valid for 7 days and transactions can typically take up to 2 weeks to fully conclude. Do inform our partner dealer your intended handover date before committing to the sale of your vehicle. Some of our sellers were able to handover their vehicles up to 1 month later, but do note that the final sale price would have to be adjusted for depreciation.',
  },
  {
    title:
      'What is my car\'s trade in value? Can I trade in or sell my car with an outstanding hire purchase loan? Are there any hidden fees involved for doing so?',
    content:
      'All our dealer partners are equipped to take care of this without any additional charges unlike other platforms. They will work with your bank on the redemption of your outstanding hire purchase loan and any surplus balances due will be paid to you during the vehicle handover.',
  },
  {
    title: 'Who will handle the paperwork?',
    content:
      'We only work with trusted and reputable LTA-approved partners that are equipped to help you handle all the necessary paperwork and vehicle transfer. Just sit back, relax and leave the legwork to them.',
  },
];

const CarValuation = () => {
  const formWrapperRef = useRef();
  const [focusForm, setFocusForm] = useState(false);

  return (
    <div className="lead-page-container">
      <Header
        openKey={'sell-your-car'}
        activeKey={'/car-valuation-singapore'}
        hideTicker
      />
      <div className="content-wrapper fluid">
        <div className="app-content">
          <div className="lead-page-wrapper">
            <div className="lead-page-hero-section flex justify-center">
              <div className="lead-page-large-section">
                <Row>
                  <Col xs={{ span: 24 }} lg={{ span: 12 }} className="mb-2">
                    <div className="text-center lg:text-left">
                      <h1 className="car-valuation-title">
                        Get the highest car price
                        <br className="hidden lg:block" /> valuation in
                        Singapore
                      </h1>
                      <h3>Find out how much your car is worth</h3>
                      <img
                        className="hidden lg:block absolute"
                        alt="Car Price Valuation in Singapore"
                        src={carValuationMin}
                        loading="lazy"
                      />
                    </div>
                  </Col>
                  <Col xs={{ span: 24 }} lg={{ span: 12 }} ref={formWrapperRef}>
                    <LeadPageCarValuationForm
                      focus={focusForm}
                      title={'Free car valuation with no obligations!'}
                      serviceRequired={'Valuation'}
                    />
                  </Col>
                </Row>
              </div>
            </div>
            <LeadPageHzSection
              title={
                <span>
                  How to get the highest car valuation
                  <br className="hidden lg:block" /> in Singapore?
                </span>
              }
              data={carValuationFirstSectionData}
            />
            <div className="car-valuation-proof-section">
              <div className="car-valuation-section">
                <LeadPageProofSection />
              </div>
            </div>
            <div>
              <LeadPageTabSlider
                title="Why trade in your car online with OneShift?"
                data={tabData}
              />
            </div>
            <div className="pt-12 pb-12 bg-light">
              <LeadPageUserReview
                title="What our happy sellers say!"
                data={userReviews}
              />
            </div>
            <div className="pt-12 pb-12">
              <LeadPageFaq data={faqData} image={faqImg} />
            </div>
            <div className="pt-12 pb-12 bg-light">
              <LeadPageServices />
            </div>
            <div>
              <LeadPageFooter
                quoteButtonText={'Get quote'}
                mainFormRef={formWrapperRef}
                onClickGetQuote={() => setFocusForm(true)}
                guideSection={
                  <>
                    <h2 className="text-center mt-6 mb-6">
                      Step by step guide to get the highest car valuation in
                      Singapore
                    </h2>
                    <div>
                      <p className="text-justify">
                        You will get a much higher price than market value, when you get your car
                        valuation from a large number of car dealers. Car
                        dealers will value your car more when there is
                        competition. To get the highest valuation or price for
                        your car, you will need to do the following steps:
                      </p>
                      <ol className="list-decimal pl-4">
                        <li>
                          Find out the car depreciation, parf value and current selling price of your car make and model on platforms like Carousell
                        </li>
                        <li>
                          Prepare your licence plate and submit your car details
                          to OneShift
                        </li>
                        <li>
                          Your car will be submitted to 500+ dealers to bid
                          automatically for your car, creating competition and
                          in turn giving you the highest valuation for your car.
                        </li>
                        <li>
                          Choose the highest price and we will arrange for a
                          non-obligatory viewing. if you decide not to sell
                          through our network of dealers, OneShift can also help
                          you sell your car on your behalf.
                        </li>
                      </ol>
                    </div>
                  </>
                }
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Head = ({ location, params, data }) => {
  const title = "Free Car Valuation Singapore | Instant Car Price Check | OneShift by Carousell";
  const description = "Get accurate car valuation in Singapore within 24 hours. Compare offers from 500+ trusted dealers. Free, no-obligation car price check with expert market analysis.";
  const keywords = "car valuation Singapore, car price check, vehicle valuation, car worth, sell car price, car dealer valuation";
  const staticImage = "/images/carousell-os-car-valuation-fb-cover.jpeg";

  const structuredDataWithoutUrl = (siteUrl) => [
    createOneShiftAutoDealerData({
      url: `${siteUrl}${location.pathname}`,
      description,
      services: [
        {
          name: "Car Valuation",
          description: "Free car valuation with competitive offers from 500+ trusted dealers"
        },
        {
          name: "Car Inspection",
          description: "Professional car inspection and condition assessment"
        },
        {
          name: "Car Selling Consultation",
          description: "Expert consultation on car selling options and market value"
        }
      ]
    }),
    createFAQData([
      {
        question: "How does OneShift car valuation work?",
        answer: "Submit your car details through our online form and receive competitive offers from our network of 500+ trusted dealers within 24 hours. Our service is free and comes with no obligations."
      },
      {
        question: "Is the car valuation service free?",
        answer: "Yes, our car valuation service is completely free. You can get multiple offers from trusted dealers with no obligation to sell."
      },
      {
        question: "How long does it take to get a car valuation?",
        answer: "You'll receive competitive offers within 24 hours after submitting your car details."
      }
    ]),
    createBreadcrumbData([
      { name: "Home", path: "/" },
      { name: "Car Valuation", path: location.pathname }
    ], siteUrl)
  ];

  return (
    <SEO 
      title={title}
      description={description}
      staticImage={staticImage}
      pathname={location.pathname}
      keywords={keywords}
      ogType="website"
      getStructuredData={structuredDataWithoutUrl}
    />
  );
};

export default CarValuation;
